/**
 * Node and link kinds as the GUI shows them. The base knows no kind: they come from the core
 * (`known`, the built-in plugins' kinds, in the graph snapshot), from the graph itself
 * (`graph.kinds`, saved with the project) and from the installed plugins that are ready.
 *
 * A kind may be `like` another and then follows its rules (an abstract class is a class); what
 * a kind brings besides its rules is its name, its class-diagram stereotype, its shape in the
 * nodal view and its line and arrow head. A kind nobody defines is still a kind: it has no
 * rules and the plain look. Choosing a plugin's kind the graph does not define yet defines it
 * in the same edit ({@link nodeKindEdits}), so the project keeps working without the plugin.
 */
import type { ArrowHead } from "@protocol/ArrowHead";
import type { Graph } from "@protocol/Graph";
import type { GraphEdit } from "@protocol/GraphEdit";
import type { Kinds } from "@protocol/Kinds";
import type { Link } from "@protocol/Link";
import type { LinkKindDef } from "@protocol/LinkKindDef";
import type { Node } from "@protocol/Node";
import type { NodeKindDef } from "@protocol/NodeKindDef";
import type { NodeShape } from "@protocol/NodeShape";

/** The shape a node is drawn with in the nodal view. */
export type ShapeName = NodeShape;

/** The stereotype that means "none" (UML's plain class), as `da_types::NO_STEREOTYPE`. */
export const NO_STEREOTYPE = "-";

/** How deep `like` is followed (a longer chain, or a circle, stops there), as the core does. */
const MOST_LIKES = 8;

/** Whether links of kind `kind` are shown with the filters `linkKinds` (a kind they do not name is shown). */
export function linkKindShown(linkKinds: Record<string, boolean>, kind: string): boolean {
  return linkKinds[kind] !== false;
}

/** No kinds at all. */
export const NO_KINDS: Kinds = { nodes: [], links: [] };

/** A node kind, ready to show and pick. */
export interface NodeKindView {
  /** Its id (`class`, `service`, `chapter`). */
  id: string;
  /** What people see (`Service`). */
  label: string;
  /** The kind it follows the rules of, if any (`interface` for a «service»). */
  like: string | null;
  /** The class-diagram stereotype (`«service»`), `null` for none. */
  stereotype: string | null;
  /** Its name is written in italic on its class-diagram box (UML's abstract class). */
  italic: boolean;
  /** Its shape in the nodal view. */
  shape: ShapeName;
  /** Whether a built-in plugin brings it (the core knows it). */
  builtin: boolean;
  /** The plugin that brings it, if one does. */
  plugin: string | null;
  /** What it is for (empty when nobody said). */
  note: string;
}

/**
 * How strongly a link kind ties its ends, which picks its colour and weight in the nodal view:
 * `hierarchy` (acyclic, like inheritance), `rule` (it may only join some kinds, like a
 * dependency on a library), `plain` (anything to anything).
 */
export type LinkTone = "hierarchy" | "rule" | "plain";

/** A link kind, ready to show and pick. */
export interface LinkKindView {
  /** Its id (`uses`, `calls`). */
  id: string;
  /** Its button label (`Uses`, `Calls`). */
  label: string;
  /** The verb in sentences (`uses`, `calls`). */
  verb: string;
  /** The same link read from its other end, as a heading (`Extended by`; `Calls (to here)` when nobody said). */
  inverse: string;
  /** The kind it follows the rules of, if any. */
  like: string | null;
  /** How its line is drawn. */
  line: "solid" | "dashed" | "dotted";
  /** Its tip. */
  head: ArrowHead;
  /** Its colour and weight in the nodal view. */
  tone: LinkTone;
  /** Whether a built-in plugin brings it (the core knows it). */
  builtin: boolean;
  /** The plugin that brings it, if one does. */
  plugin: string | null;
  /** What it means (empty when nobody said). */
  note: string;
}

/** `depends on` → `Depends on`. */
const capitalized = (text: string): string => text.charAt(0).toUpperCase() + text.slice(1);

/**
 * Every kind that can be used: the `known` ones (built-in plugins'), the graph's own, then
 * the plugins' ones neither has. A known kind wins over the graph's definition of the same id.
 */
export function allKinds(known: Kinds | undefined, graph: Kinds | undefined, plugins: Kinds = NO_KINDS): Kinds {
  const lists = [known ?? NO_KINDS, graph ?? NO_KINDS, plugins];
  const nodes: NodeKindDef[] = [];
  const links: LinkKindDef[] = [];
  for (const kinds of lists) {
    for (const k of kinds.nodes) if (!nodes.some((o) => o.id === k.id)) nodes.push(k);
    for (const k of kinds.links) if (!links.some((o) => o.id === k.id)) links.push(k);
  }
  return { nodes, links };
}

/** Node kind `kind`, then the kind it is like, and so on (`kind` alone when undefined). */
export function nodeChain(all: Kinds, kind: string): string[] {
  const chain = [kind];
  while (chain.length < MOST_LIKES) {
    const next = all.nodes.find((k) => k.id === chain[chain.length - 1])?.like;
    if (!next || chain.includes(next)) break;
    chain.push(next);
  }
  return chain;
}

/** Link kind `kind`'s definition, then the one of the kind it is like, and so on. */
function linkChain(all: Kinds, kind: string): LinkKindDef[] {
  const chain: LinkKindDef[] = [];
  let id: string | undefined = kind;
  while (id && chain.length < MOST_LIKES) {
    const def = all.links.find((k) => k.id === id);
    if (!def || chain.includes(def)) break;
    chain.push(def);
    id = def.like;
  }
  return chain;
}

/** Whether a node of kind `kind` counts as one of `wanted` (itself or a kind it is like); an empty `wanted` takes every kind. */
export function nodeIs(all: Kinds, kind: string, wanted: readonly string[]): boolean {
  return wanted.length === 0 || nodeChain(all, kind).some((k) => wanted.includes(k));
}

/** The rules link kind `kind` follows: the first definition along its `like`s that lists some. */
function linkRules(all: Kinds, kind: string): LinkKindDef | undefined {
  return linkChain(all, kind).find((d) => (d.allow ?? []).length > 0);
}

/** Whether a `kind` link may join a node of kind `from` to one of kind `to`, as the core checks it. */
export function linkAllowed(all: Kinds, kind: string, from: string, to: string): boolean {
  const rules = linkRules(all, kind);
  return !rules || (rules.allow ?? []).some((a) => nodeIs(all, from, a.from ?? []) && nodeIs(all, to, a.to ?? []));
}

/**
 * The link kind offered first for a link from a `from` node to a `to` node: the first one that
 * suggests itself for `to`'s kind (or a kind it is like) and whose rules allow it, else the
 * first kind with no rules, else none. The same choice as the core's `Kinds::suggested_link`.
 */
export function suggestedLink(all: Kinds, from: string, to: string): string | null {
  const chain = nodeChain(all, to);
  const wanted = all.links.find((k) => linkAllowed(all, k.id, from, to) && (k.suggested_to ?? []).some((s) => chain.includes(s)));
  const free = all.links.find((k) => !linkRules(all, k.id));
  return (wanted ?? free)?.id ?? null;
}

/** The view of one node kind, `known` saying whether a built-in plugin brings it. */
function nodeView(all: Kinds, k: NodeKindDef, known: boolean): NodeKindView {
  const word = (k.stereotype ?? "").trim();
  const stereotype = word === NO_STEREOTYPE ? null : `«${word || k.label.toLowerCase()}»`;
  const shape = nodeChain(all, k.id)
    .map((id) => all.nodes.find((d) => d.id === id)?.shape)
    .find((s) => !!s);
  return { id: k.id, label: k.label, like: k.like ?? null, stereotype, italic: !!k.italic, shape: shape ?? "circle", builtin: known, plugin: k.plugin ?? null, note: k.note ?? "" };
}

/** The view of one link kind, `known` saying whether a built-in plugin brings it. */
function linkView(all: Kinds, k: LinkKindDef, known: boolean): LinkKindView {
  const chain = linkChain(all, k.id);
  const line = chain.map((d) => d.line).find((l) => !!l) ?? "solid";
  const head = chain.map((d) => d.head).find((h) => !!h) ?? "open";
  const tone: LinkTone = chain.some((d) => d.acyclic) ? "hierarchy" : linkRules(all, k.id) ? "rule" : "plain";
  const inverse = k.inverse ? capitalized(k.inverse) : `${capitalized(k.label)} (to here)`;
  return { id: k.id, label: capitalized(k.label), verb: k.label, inverse, like: k.like ?? null, line, head, tone, builtin: known, plugin: k.plugin ?? null, note: k.note ?? "" };
}

/** Every node kind that can be picked, in their order (the known ones first). */
export function nodeKinds(all: Kinds, known: Kinds = NO_KINDS): NodeKindView[] {
  return all.nodes.map((k) => nodeView(all, k, known.nodes.some((o) => o.id === k.id)));
}

/** Every link kind that can be picked, in their order (the known ones first). */
export function linkKinds(all: Kinds, known: Kinds = NO_KINDS): LinkKindView[] {
  return all.links.map((k) => linkView(all, k, known.links.some((o) => o.id === k.id)));
}

/** The kind a node is (a plain view named after its id when nobody defines it). */
export function nodeKindOf(all: Kinds | undefined, node: Pick<Node, "kind">, known: Kinds = NO_KINDS): NodeKindView {
  const kinds = all ?? NO_KINDS;
  const def = kinds.nodes.find((k) => k.id === node.kind);
  if (def) return nodeView(kinds, def, known.nodes.some((o) => o.id === def.id));
  return { id: node.kind, label: capitalized(node.kind), like: null, stereotype: `«${node.kind}»`, italic: false, shape: "circle", builtin: false, plugin: null, note: "" };
}

/** The kind a link is (a plain view named after its id when nobody defines it). */
export function linkKindOf(all: Kinds | undefined, link: Pick<Link, "kind">, known: Kinds = NO_KINDS): LinkKindView {
  const kinds = all ?? NO_KINDS;
  const def = kinds.links.find((k) => k.id === link.kind);
  if (def) return linkView(kinds, def, known.links.some((o) => o.id === def.id));
  return { id: link.kind, label: capitalized(link.kind), verb: link.kind, inverse: `${capitalized(link.kind)} (to here)`, like: null, line: "solid", head: "open", tone: "plain", builtin: false, plugin: null, note: "" };
}

/** A `define_kinds` edit for the plugin kinds `nodes` and `links` that neither the graph nor the core knows yet (none when they do). */
function defineMissing(graph: Pick<Graph, "kinds">, plugins: Kinds, known: Kinds, nodes: string[], links: string[]): GraphEdit[] {
  const has = (list: { id: string }[] | undefined, id: string) => !!list?.some((o) => o.id === id);
  const kinds: Kinds = {
    nodes: plugins.nodes.filter((k) => nodes.includes(k.id) && !has(graph.kinds?.nodes, k.id) && !has(known.nodes, k.id)),
    links: plugins.links.filter((k) => links.includes(k.id) && !has(graph.kinds?.links, k.id) && !has(known.links, k.id)),
  };
  return kinds.nodes.length || kinds.links.length ? [{ edit: "define_kinds", kinds }] : [];
}

/** The edits making node `id` of the kind `kindId`, as one undo (a plugin's kind defined first). */
export function nodeKindEdits(graph: Pick<Graph, "kinds">, plugins: Kinds, known: Kinds, id: string, kindId: string): GraphEdit[] {
  return [...defineMissing(graph, plugins, known, [kindId], []), { edit: "update_node", id, patch: { kind: kindId } }];
}

/** The edits making `link` of the kind `kindId`, as one undo (a plugin's kind defined first). */
export function linkKindEdits(graph: Pick<Graph, "kinds">, plugins: Kinds, known: Kinds, link: Link, kindId: string): GraphEdit[] {
  return [...defineMissing(graph, plugins, known, [], [kindId]), { edit: "update_link", link, patch: { kind: kindId } }];
}

/** The edits adding `node`, as one undo (its kind defined first when it is a plugin's). */
export function addNodeEdits(graph: Pick<Graph, "kinds">, plugins: Kinds, known: Kinds, node: Node): GraphEdit[] {
  return [...defineMissing(graph, plugins, known, [node.kind], []), { edit: "add_node", node, links: [] }];
}

/** The edits adding `link`, as one undo (its kind defined first when it is a plugin's). */
export function addLinkEdits(graph: Pick<Graph, "kinds">, plugins: Kinds, known: Kinds, link: Link): GraphEdit[] {
  return [...defineMissing(graph, plugins, known, [], [link.kind]), { edit: "add_link", link }];
}

/** The `define_kinds` edit the plugin kinds `nodes` and `links` need before the graph can use them (used to apply AI suggestions). */
export function defineFor(graph: Pick<Graph, "kinds">, plugins: Kinds, known: Kinds, nodes: string[], links: string[]): GraphEdit[] {
  return defineMissing(graph, plugins, known, nodes, links);
}
